<template>
  <Card v-loading="loading" class="new-release">
    <div class="new-release__header">
      <h2 class="new-release__title">{{ $t('APP.RELEASES.UNCOMMITTED') }}</h2>
    </div>

    <ReleaseBundle v-if="shouldShowBundleBox" :bundle="bundle" />

    <template v-else>
      <ReleaseFileUploader
        v-if="shouldShowUploader"
        :release-id="uncommittedRelease.token"
        :can-upload-bundle="!hasPackages"
        @add-package="addPackage"
      />

      <NewReleasePackagesTable
        :packages="uncommittedRelease.packages"
        :release-id="uncommittedRelease.token"
        :release-actions="uncommittedRelease.actions"
        class="new-release__package-table"
        @remove-package="removePackage"
        @detail-click="emitDetailEvent"
      />

      <UsedFeaturesAndLibraries
        v-if="uncommittedRelease.featuresAndLibraries"
        v-bind="uncommittedRelease.featuresAndLibraries"
        class="new-release__alert"
      />

      <NewReleaseChangelogs v-model="changelogs" />

      <div class="new-release__auto-publish">
        <header class="new-release__heading">
          {{ $t('APP.DRAFT.STEPS.FINAL_CHECK.PUBLISHING_TYPE') }}
          <Hint :message="$t('HINTS.APPS.RELEASES.AUTO_PUBLISH')" />
        </header>

        <ElSwitch
          v-model="autoPublish"
          :active-text="$t('APP.RELEASES.AUTO_PUBLISH')"
          @change="handleAutoPublishChange"
        />
      </div>

      <div
        v-if="app.alphaEnabled || app.betaEnabled"
        class="new-release__auto-publish"
      >
        <header class="new-release__heading">
          {{ $t('APP.DRAFT.STEPS.FINAL_CHECK.PUBLISHING_GROUP') }}
          <Hint :message="$t('HINTS.APPS.RELEASES.PUBLISH_GROUP')" />
        </header>

        <ElRadioGroup
          v-model="publishGroup"
          class="new-release__auto-publish"
          @change="updateGroupPublish"
        >
          <ElRadio v-if="app.alphaEnabled" :label="'alpha'">
            {{ $t('APPS.DRAFT.STEPS.FINAL_CHECK.ALPHA') }}
          </ElRadio>

          <ElRadio v-if="app.betaEnabled" :label="'beta'">
            {{ $t('APPS.DRAFT.STEPS.FINAL_CHECK.BETA') }}
          </ElRadio>

          <ElRadio :label="'public'">
            {{ $t('APPS.DRAFT.STEPS.FINAL_CHECK.PUBLIC') }}
          </ElRadio>
        </ElRadioGroup>
      </div>

      <StagedRollout
        v-if="shouldShowStagedRollot"
        :disabled="!canUseStagedRollout"
        :percentage.sync="stagedRolloutPercentage"
        :start="stagedRollout.start"
        :step="stagedRollout.step"
      />
    </template>

    <ElSelect
      v-if="app.alphaEnabled && publishGroup === 'alpha'"
      v-model="selectedAlphaRelease"
      class="new-release__select"
      filterable
      :placeholder="$t('PICK_A_ALPHA_GROUP')"
    >
      <ElOption
        v-for="release in alphaGroup"
        :key="release.id"
        :value="release.id"
        :label="`${release.name}`"
      />
    </ElSelect>
    <ElButton
      v-if="app.alphaEnabled && publishGroup === 'alpha'"
      type="text"
      nonce
      size="big"
      icon="el-icon-plus"
      @click="showAddAlphaGroupModal = true"
    >
      {{ $t('ADD_ALPHA_GROUP') }}
    </ElButton>

    <div class="new-release__action-group">
      <NewReleaseCommitButton
        :release="uncommittedRelease"
        :disabled="!hasPackages"
        :loading.sync="loading"
        :changelogs="changelogs"
        :staged-rollout-percentage="stagedRolloutPercentage"
        :release-groups="selectedRelease ? [selectedRelease] : []"
      />
      <NewReleaseDeleteButton :disabled="!canDeleteRelease" />
    </div>
    <AddAlphaGroupModal
      v-model="showAddAlphaGroupModal"
      :package-name="packageName"
      @success="handleAlphaGroupSuccess"
    />

    <DisableAutoPublishModal
      v-model="showDisableAutoPublishModal"
      @confirm="confirmDisableAutoPublish"
      @cancel="cancelDisableAutoPublish"
    />
  </Card>
</template>

<script>
import Card from '@@/common/Card';
import form from '@/mixins/form';
import packageName from '@/mixins/packageName';
import NewReleaseCommitButton from './NewReleaseCommitButton';
import NewReleaseDeleteButton from './NewReleaseDeleteButton';
import NewReleasePackagesTable from './NewReleasePackagesTable';
import NewReleaseChangelogs from './NewReleaseChangelogs';
import ReleaseFileUploader from './ReleaseFileUploader';
import StagedRollout from './StagedRollout';
import ReleaseBundle from './ReleaseBundle';
import Hint from '@@/common/Hint';
import UsedFeaturesAndLibraries from '@@/app/common/UsedFeaturesAndLibraries';

import { BUNDLE_STATES } from '@@/app/common/constants';
import {
  UPDATE_RELEASE,
  APP_DETAILS,
  UNCOMMITTED_RELEASE,
  UPDATE_UNCOMMITTED_RELEASE,
  ALPHA_GROUP,
  BETA_GROUP,
} from '@/store/types';
import { mapActions, mapGetters } from 'vuex';
import AddAlphaGroupModal from '../Group/AddAlphaGroupModal.vue';
import DisableAutoPublishModal from './DisableAutoPublishModal.vue';
export default {
  name: 'NewRelease',

  components: {
    Card,
    NewReleaseCommitButton,
    NewReleaseDeleteButton,
    NewReleasePackagesTable,
    NewReleaseChangelogs,
    StagedRollout,
    ReleaseBundle,
    Hint,
    ReleaseFileUploader,
    UsedFeaturesAndLibraries,
    AddAlphaGroupModal,
    DisableAutoPublishModal,
  },

  mixins: [packageName, form],

  data() {
    return {
      loading: false,
      autoPublish: true,
      publishGroup: 'public',
      changelogs: {
        changelogEn: '',
        changelogFa: '',
      },
      errors: [],
      stagedRolloutPercentage: 100,
      selectedAlphaRelease: null,
      selectedBetaRelease: null,
      showAddAlphaGroupModal: false,
      selectedRelease: null,
      showDisableAutoPublishModal: false,
    };
  },
  computed: {
    ...mapGetters({
      app: APP_DETAILS,
      uncommittedRelease: UNCOMMITTED_RELEASE,
      alphaGroup: ALPHA_GROUP,
      betaGroup: BETA_GROUP,
    }),
    hasPackages() {
      return this.uncommittedRelease?.packages.length > 0;
    },
    canDeleteRelease() {
      return this.uncommittedRelease?.actions?.delete;
    },
    shouldShowStagedRollot() {
      return this.app.published === 'P';
    },
    canUseStagedRollout() {
      return this.app.canStageRollOut;
    },
    stagedRollout() {
      return this.uncommittedRelease?.stagedRollout || {};
    },
    bundle() {
      return this.uncommittedRelease?.bundle;
    },
    shouldShowBundleBox() {
      const { bundle } = this;
      return bundle && bundle.state !== BUNDLE_STATES.DONE;
    },
    shouldShowUploader() {
      return !this.bundle;
    },
  },
  watch: {
    selectedAlphaRelease(newValue) {
      this.selectedRelease = newValue;
    },
    selectedBetaRelease(newValue) {
      this.selectedRelease = newValue;
    },
  },
  created() {
    this.initialData();
  },
  methods: {
    ...mapActions({
      updateRelease: UPDATE_RELEASE,
      updateNewReleaseData: UPDATE_UNCOMMITTED_RELEASE,
    }),
    emitDetailEvent(currentPackage) {
      this.$emit('detail-click', currentPackage);
    },
    handleAutoPublishChange(value) {
      if (!value) {
        this.autoPublish = true;
        this.showDisableAutoPublishModal = true;
      } else {
        this.updateAutoPublish(value);
      }
    },
    confirmDisableAutoPublish() {
      this.showDisableAutoPublishModal = false;
      this.$nextTick(() => {
        this.autoPublish = false;
        this.updateAutoPublish(false);
      });
    },
    cancelDisableAutoPublish() {
      this.showDisableAutoPublishModal = false;
      this.autoPublish = true;
    },
    updateAutoPublish(value) {
      const prevValue = !value;
      const queryParams = {
        packageName: this.packageName,
        token: this.uncommittedRelease.token,
      };

      return this.updateRelease({
        queryParams,
        updateBody: { autoPublish: value },
      }).catch(({ message }) => {
        if (message) {
          this.$message.error(message);
        }
        this.autoPublish = prevValue;
      });
    },
    updateGroupPublish(value) {
      if (value === 'beta') {
        this.selectedAlphaRelease = null;
        this.selectedBetaRelease = this.betaGroup?.[0]?.id;
      }
      if (value === 'alpha') {
        this.selectedBetaRelease = null;
        this.selectedAlphaRelease = this.alphaGroup?.[0]?.id;
      }
      if (value === 'public') {
        this.selectedBetaRelease = null;
        this.selectedAlphaRelease = null;
      }
    },
    async initialData() {
      this.autoPublish = this.uncommittedRelease.autoPublish || true;
    },
    addPackage(packageItem) {
      const currentPackages = this.uncommittedRelease?.packages || [];

      this.updateNewReleaseData({
        packages: [...currentPackages, packageItem],
      });
    },
    removePackage(removedPackage) {
      const packages = this.uncommittedRelease?.packages?.filter(
        pkg => pkg.token !== removedPackage.token,
      );

      this.updateNewReleaseData({
        packages,
      });
    },
    async handleAlphaGroupSuccess() {
      await this.getGroupRelease(this.packageName);
    },
  },
};
</script>

<style lang="scss">
.new-release {
  &__header {
    display: flex;
    align-items: center;
    margin-bottom: $global-margin * 2;
  }

  &__title {
    color: $wet-asphalt;
    font-size: rem-calc(20);
    font-weight: bold;
    margin: 0;
  }

  &__action-group {
    margin-top: $global-margin * 2;
    display: flex;
    align-items: center;
  }

  &__package-table {
    margin-bottom: 2 * $global-margin;
  }

  &__auto-publish {
    margin-left: auto;
    margin-top: $global-margin;
    @include rtl() {
      margin-left: 0;
      margin-right: auto;
    }
  }

  &__delete-confirm-button,
  &__delete-confirm-button:focus,
  &__delete-confirm-button:hover {
    background-color: $alizarin;
    border: 0;
  }

  &__note {
    margin-top: $global-margin;
  }

  &__alert {
    margin-bottom: $global-margin;
  }

  &__select {
    width: 25%;
    margin-top: 2rem;
  }

  .el-switch__label {
    margin-right: calc($global-margin / 2);

    * {
      font-size: 0.8rem;
    }
  }

  .el-icon-question {
    vertical-align: middle;
    margin: 0 $global-margin / 2;
  }

  .new-feature-badge sup {
    top: 0;
    margin-right: $global-margin * 0.6;

    @include rtl() {
      margin-right: 0;
      margin-left: $global-margin * 0.6;
    }
  }
}
</style>
